"use client";

import { useState, useEffect, useCallback } from "react";
import { useUser } from "@/lib/context/user-context";
import { LibraryBig, PlayCircle, CheckCircle, ArrowRight, Play, Pause, History, Podcast, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { formatTime } from "@/utils/device-detection";
// import Image from "next/image";
import Link from "next/link";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { useI18n } from "@/lib/i18n";
import { usePlayer } from "@/contexts/player-context";
import { MobileMenuButton } from "@/components/ui/mobile-menu-button";
import { useIsMobile } from "@/hooks/use-mobile";

// 用于避免 hydration 不匹配的 hook
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}

interface HistoryItem {
  id: string;
  episodeId: string;
  episodeTitle: string;
  podcastTitle?: string;
  thumbnailUrl?: string;
  description?: string;  // 🔥 新增：episode描述
  userPlaybackPosition: number;
  mediaFileDuration: number;
  completed: boolean;
  lastUpdated: string;
  progressPercentage: number;
}

// 检测内容类型的辅助函数
function getContentType(item: HistoryItem): 'video' | 'podcast' {
  // 检查episodeId是否为YouTube视频ID格式 (11位字符)
  const isYouTubeVideoId = /^[a-zA-Z0-9_-]{11}$/.test(item.episodeId);
  
  // YouTube视频通常没有podcastTitle，或者episodeId符合YouTube格式
  const isLikelyVideo = isYouTubeVideoId || (!item.podcastTitle && item.episodeId.length === 11);
  
  return isLikelyVideo ? 'video' : 'podcast';
}

// 安全地处理和格式化 HTML 描述 - 移到组件外部避免热重载问题
function sanitizeAndFormatDescription(description: string): string {
  if (!description) return '';
  
  // 基本的 HTML 清理，保留 <p> 标签但转换为内联显示
  let cleaned = description
    // 移除危险标签
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // 将 <p> 标签转换为内联元素，用分隔符分开
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, ' • ')
    // 移除其他 HTML 标签但保留内容
    .replace(/<br\s*\/?>/gi, ' • ')
    .replace(/<\/?(div|span|strong|em|b|i|u)[^>]*>/gi, '')
    // 清理多余的分隔符
    .replace(/(\s*•\s*)+/g, ' • ')
    .replace(/^•\s*/, '')
    .replace(/\s*•$/, '')
    // 清理多余空格
    .replace(/\s+/g, ' ')
    .trim();
  
  // 如果描述太长，确保省略号在合适的位置
  if (cleaned.length > 200) {
    // 在最后一个完整的歌曲名称后截断
    const lastSeparator = cleaned.lastIndexOf(' • ', 200);
    if (lastSeparator > 100) {
      cleaned = cleaned.substring(0, lastSeparator) + '...';
    } else {
      cleaned = cleaned.substring(0, 200) + '...';
    }
  }
  
  return cleaned;
}

export default function HistoryPage() {
  const { t, lang } = useI18n() as any;
  const { user, isLoading: userLoading } = useUser();
  const { 
    setCurrentEpisode, 
    play, 
    pause, 
    isPlaying, 
    currentEpisode, 
    openFullPlayer,
    showPlayer,
    setIsLoading: setPlayerLoading,
    closeFullPlayer,
    setCleanReadData,
  } = usePlayer();
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEpisodeId, setLoadingEpisodeId] = useState<string | null>(null);
  const [filter, ] = useState<'all' | 'completed' | 'in-progress'>('all');
  
  // 🔥 移动端直接打开播放器而不是跳转
  const hasMounted = useHasMounted();
  const isMobile = useIsMobile();
  const allowDirectPlay = hasMounted && isMobile;

  const mobileMenuButton = (
    <MobileMenuButton ariaLabel={lang === 'chinese' ? '打开菜单' : 'Open menu'} />
  );

  useEffect(() => {
    if (user) {
      loadHistoryItems();
    }
  }, [user]);

  const loadHistoryItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/user/${user?.id}/history`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Debug: Log the loaded data
      console.log('📚 Loaded history items:', data.historyItems);
      data.historyItems?.forEach((item: any, index: number) => {
        console.log(`📚 Item ${index}:`, {
          title: item.episodeTitle,
          thumbnailUrl: item.thumbnailUrl,
          podcastTitle: item.podcastTitle,
        });
      });
      
      setHistoryItems(data.historyItems || []);
    } catch (error) {
      console.error("Failed to load history items:", error);
      // Set empty array on error
      setHistoryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = historyItems.filter(item => {
    switch (filter) {
      case 'completed':
        return item.completed;
      case 'in-progress':
        return !item.completed && item.userPlaybackPosition > 0;
      default:
        return true;
    }
  });

  // 🔥 桌面端：跳转到 episodes 页面
  const handleContinueWatching = (episodeId: string, position: number) => {
    // Navigate to episode with timestamp
    window.location.href = `/episodes/${episodeId}?t=${position}`;
  };

  // 🔥 播放器播放逻辑（移动端和桌面端通用）
  const handlePlayInBottomPlayer = useCallback(async (item: HistoryItem, shouldOpenFullPlayer = false) => {
    console.log('🎵 Playing in player:', item.episodeTitle, { shouldOpenFullPlayer });
    
    // 设置加载状态
    setLoadingEpisodeId(item.episodeId);
    
    // 🔥 移动端：先显示播放器并打开全屏对话框
    if (shouldOpenFullPlayer) {
      showPlayer();
      openFullPlayer();
      setPlayerLoading(true);
    }
    
    // 🔥 先设置占位数据，让用户立即看到内容
    const placeholder = {
      shareId: item.episodeId,
      title: item.episodeTitle,
      author: item.podcastTitle,
      thumbnail: item.thumbnailUrl,
      duration: item.mediaFileDuration,
      description: item.description,
      hydrationPending: true,
      isHydrationPlaceholder: true,
      content: {
        shareId: item.episodeId,
        title: item.episodeTitle,
        author: item.podcastTitle,
        thumbnail: item.thumbnailUrl,
        duration: item.mediaFileDuration,
        description: item.description,
        hydrationPending: true,
        isHydrationPlaceholder: true,
      },
    };
    
    if (shouldOpenFullPlayer) {
      setCurrentEpisode(placeholder);
      setCleanReadData(null);
    }
    
    try {
      // 获取完整的 episode 数据（包含音频 URL）
      const response = await fetch(`/api/episodes/${item.episodeId}`);
      if (!response.ok) {
        console.error('Failed to fetch episode data');
        if (shouldOpenFullPlayer) {
          closeFullPlayer();
        }
        return;
      }
      
      const apiResponse = await response.json();
      console.log('🎵 Episode data fetched:', apiResponse);
      
      // 构建 episode 数据
      const shareData = apiResponse?.content ?? {};
      const shareDataContent = shareData?.content ?? {};
      
      const episodeData = {
        ...shareData,
        hydrationPending: false,
        isHydrationPlaceholder: false,
        generationRecords: apiResponse?.generationRecords,
        content: {
          ...shareDataContent,
          hydrationPending: false,
          isHydrationPlaceholder: false,
          generationRecords: apiResponse?.generationRecords,
        },
      };
      
      // 设置当前 episode 和开始时间
      const startTime = item.completed ? 0 : item.userPlaybackPosition;
      setCurrentEpisode(episodeData, startTime);
      setCleanReadData(shareDataContent?.cleanRead ?? null);
      
      // 自动开始播放
      setTimeout(() => {
        play();
      }, 100);
    } catch (error) {
      console.error('Error playing episode:', error);
      
      if (shouldOpenFullPlayer) {
        closeFullPlayer();
      }
      
      // 降级处理：使用现有数据尝试播放
      const episodeData = {
        shareId: item.episodeId,
        hydrationPending: false,
        isHydrationPlaceholder: false,
        content: {
          shareId: item.episodeId,
          title: item.episodeTitle,
          podcastTitle: item.podcastTitle,
          author: item.podcastTitle,
          thumbnail: item.thumbnailUrl,
          duration: item.mediaFileDuration,
          description: item.description,
          hydrationPending: false,
          isHydrationPlaceholder: false,
        }
      };
      
      const startTime = item.completed ? 0 : item.userPlaybackPosition;
      setCurrentEpisode(episodeData, startTime);
      
      setTimeout(() => {
        play();
      }, 100);
    } finally {
      // 清除加载状态
      setLoadingEpisodeId(null);
      if (shouldOpenFullPlayer) {
        setPlayerLoading(false);
      }
    }
  }, [setCurrentEpisode, play, openFullPlayer, showPlayer, setPlayerLoading, closeFullPlayer, setCleanReadData]);

  // 🔥 移动端：Continue 按钮 - 打开全屏播放器并从上次位置继续
  const handleMobileContinue = useCallback(async (item: HistoryItem) => {
    await handlePlayInBottomPlayer(item, true);
  }, [handlePlayInBottomPlayer]);

  // 🔥 移动端：View 按钮 - 打开全屏播放器
  const handleMobileView = useCallback(async (item: HistoryItem) => {
    await handlePlayInBottomPlayer(item, true);
  }, [handlePlayInBottomPlayer]);

  // 检查当前播放的是否是这个 episode
  const isCurrentEpisode = (episodeId: string) => {
    return currentEpisode?.shareId === episodeId || 
           currentEpisode?.content?.shareId === episodeId;
  };

  // 检查是否正在播放当前 episode
  const isPlayingThisEpisode = (episodeId: string) => {
    return isCurrentEpisode(episodeId) && isPlaying;
  };

  // 🔧 修复：区分加载中和未登录状态
  if (userLoading) {
    return (
      <>
        {mobileMenuButton}
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-3">
            <SpinnerIcon size={24} color="hsl(var(--muted-foreground))" />
            <p className="pl-2">{t('history.loading')}</p>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        {mobileMenuButton}
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <CardTitle>{t('history.loginRequiredTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-4">{t('history.loginRequiredDesc')}</p>
              <Button asChild>
                <Link href="/login">Login</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      {mobileMenuButton}
      <div className="mx-auto px-3 sm:px-4 py-8">
      {/* Header */}
      <div className="sm:mb-8 mb-4 space-y-2">
        <div className="flex items-center gap-3">
          <LibraryBig className="w-8 h-8" />
          <h1 className="text-2xl sm:text-3xl font-bold">{t('history.title')}</h1>
        </div>
        <p className={`sm:text-normal text-sm text-muted-foreground ${lang === 'chinese' ? 'font-normal' : ''}`}>{t('history.subtitle')}</p>
      </div>

      {/* Filter Tabs */}
      {/* <div className="flex gap-2 mb-6">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          size="sm"
        >
          All ({historyItems.length})
        </Button>
        <Button
          variant={filter === 'in-progress' ? 'default' : 'outline'}
          onClick={() => setFilter('in-progress')}
          size="sm"
        >
          In Progress ({historyItems.filter(item => !item.completed && item.userPlaybackPosition > 0).length})
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          onClick={() => setFilter('completed')}
          size="sm"
        >
          Completed ({historyItems.filter(item => item.completed).length})
        </Button>
      </div> */}

      {/* History Items */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <SpinnerIcon size={24} color="hsl(var(--muted-foreground))" />
            <p className="pl-2">{t('history.loading')}</p>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <History className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">{t('history.noHistoryTitle')}</h3>
            <p className="text-muted-foreground mb-4">
              {filter === 'all' 
                ? t('history.noHistoryAll')
                : (lang === 'chinese' 
                    ? t('history.noHistoryFiltered', '暂无记录')
                    : t('history.noHistoryFiltered', 'No filtered episodes').replace('undefined', filter.replace('-', ' ')))}
            </p>
            <Button asChild>
              <Link href="/episodes">{lang === 'chinese' ? '浏览节目' : 'Browse Episodes'}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow rounded-2xl">
              <CardContent className="sm:p-2 p-1">
                <div className="flex gap-2 sm:gap-4">
                  {/* Thumbnail with Play Button Overlay */}
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 group">
                    {item.thumbnailUrl ? (
                      <>
                        {/* Use regular img tag for better compatibility */}
                        <img
                          src={item.thumbnailUrl}
                          alt={item.episodeTitle}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Failed to load thumbnail:', item.thumbnailUrl);
                            // Hide the image and show placeholder
                            e.currentTarget.style.display = 'none';
                            const placeholder = e.currentTarget.parentElement?.querySelector('.thumbnail-placeholder');
                            if (placeholder) {
                              (placeholder as HTMLElement).style.display = 'flex';
                            }
                          }}
                          onLoad={(e) => {
                            console.log('Successfully loaded thumbnail:', item.thumbnailUrl);
                            // Hide placeholder when image loads
                            const placeholder = e.currentTarget.parentElement?.querySelector('.thumbnail-placeholder');
                            if (placeholder) {
                              (placeholder as HTMLElement).style.display = 'none';
                            }
                          }}
                        />
                        {/* Placeholder, hidden by default when image is present */}
                        <div className="thumbnail-placeholder absolute inset-0 w-full h-full flex flex-col items-center justify-center text-center p-2 bg-gray-100" style={{ display: 'flex' }}>
                          <PlayCircle className="w-6 h-6 text-gray-400 mb-1" />
                          <span className="text-xs text-gray-500 leading-tight">{item.podcastTitle || 'Episode'}</span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-center p-2">
                        <PlayCircle className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500 leading-tight">{item.podcastTitle || 'Episode'}</span>
                      </div>
                    )}
                    
                    {/* 圆形播放按钮覆盖层 */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        disabled={loadingEpisodeId === item.episodeId}
                        onClick={(e) => {
                          e.stopPropagation(); // 防止触发卡片点击
                          if (isCurrentEpisode(item.episodeId)) {
                            // 如果是当前 episode，切换播放/暂停
                            if (isPlaying) {
                              pause();
                            } else {
                              play();
                            }
                          } else {
                            // 如果不是当前 episode，在 bottom player 中播放
                            // 🔥 移动端：同时打开全屏播放器
                            handlePlayInBottomPlayer(item, allowDirectPlay);
                          }
                        }}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg
                          ${isCurrentEpisode(item.episodeId)
                            ? 'bg-white/90 text-primary hover:bg-primary/90' // Play按钮边框填充颜色
                            : 'bg-white/90 text-gray-700 hover:bg-white hover:scale-105'
                          }
                          ${loadingEpisodeId === item.episodeId ? 'cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {loadingEpisodeId === item.episodeId ? (
                          <SpinnerIcon size="sm" color="currentColor" />
                        ) : isPlayingThisEpisode(item.episodeId) ? (
                          <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <Play className={`w-4 h-4 sm:w-5 sm:h-5 ml-0.5 ${isCurrentEpisode(item.episodeId) ? 'fill-primary' : ''}`} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex flex-col">
                    {/* 顶部：标题和播放时间 */}
                    <div className="flex items-start justify-between sm:mb-2 mb-1">
                      <div className="flex-1 min-w-0 flex items-start gap-1 sm:gap-2">
                        {/* 内容类型图标 - 移到左侧，使用温暖的主题色 */}
                        <div className="mt-0.5 flex-shrink-0">
                          {getContentType(item) === 'video' ? (
                            <div className="w-5 h-4 rounded-md bg-foreground flex items-center justify-center" title="Video Content">
                              <Video className="w-3.5 h-3.5 text-background" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-100 to-yellow-50 flex items-center justify-center" title="Podcast Content">
                              <Podcast className="w-3.5 h-3.5 text-amber-700" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="sm:font-semibold font-medium sm:text-normal text-sm leading-tight mb-1 truncate">
                            <span className="block sm:hidden">
                              {item.episodeTitle.length > 20 ? `${item.episodeTitle.substring(0, 20)}...` : item.episodeTitle}
                            </span>
                            <span className="hidden sm:block">
                              {item.episodeTitle}
                            </span>
                          </h3>
                          {item.podcastTitle && (
                            <p className="sm:text-sm text-xs text-muted-foreground truncate">
                              <span className="block sm:hidden">
                                {item.podcastTitle.length > 20 ? `${item.podcastTitle.substring(0, 20)}...` : item.podcastTitle}
                              </span>
                              <span className="hidden sm:block">
                                {item.podcastTitle}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* 右侧时间信息和状态 - 移动端优化 */}
                      <div className="flex flex-col items-end gap-0.5 ml-2 sm:ml-4 text-xs text-muted-foreground flex-shrink-0">
                        <div className="text-xs">
                          {new Date(item.lastUpdated).toLocaleDateString(lang === 'chinese' ? 'zh-CN' : 'en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="font-mono text-xs">
                          {item.completed ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              <span className="hidden sm:inline">{t('history.completed')}</span>
                            </span>
                          ) : (
                            <span>
                              <span className="hidden sm:inline">{formatTime(item.userPlaybackPosition)} / </span>
                              {Math.round((item.mediaFileDuration - item.userPlaybackPosition) / 60)}m {t('history.left')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 描述 - 小屏幕隐藏 */}
                    <div className="hidden sm:block">
                      {item.description && (
                        <p className="text-xs text-muted-foreground/80 mb-2 line-clamp-2 leading-relaxed">
                          {sanitizeAndFormatDescription(item.description)}
                        </p>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="sm:mb-2 mb-1">
                      <Progress value={item.progressPercentage} className="sm:h-1.5 h-1" />
                    </div>

                    {/* 底部操作按钮 - 移动端优化 */}
                    <div className="flex gap-0.5 sm:gap-1 mt-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={loadingEpisodeId === item.episodeId}
                        onClick={() => {
                          // 🔥 移动端：打开全屏播放器
                          if (allowDirectPlay) {
                            handleMobileContinue(item);
                          } else {
                            // 桌面端：跳转到 episodes 页面
                            handleContinueWatching(item.episodeId, item.userPlaybackPosition);
                          }
                        }}
                        className="text-xs px-1.5 py-1 h-7 flex-1 sm:flex-none min-w-0"
                      >
                        {loadingEpisodeId === item.episodeId ? (
                          <SpinnerIcon size="sm" color="currentColor" />
                        ) : item.completed ? (
                          <>
                            <PlayCircle className="w-3 h-3 mr-0.5 flex-shrink-0" />
                            <span className={`${lang === 'chinese' ? 'font-normal' : ''} truncate`}>
                              <span className="sm:hidden">Again</span>
                              <span className="hidden sm:inline">{t('history.watchAgain')}</span>
                            </span>
                          </>
                        ) : (
                          <>
                            <ArrowRight className="w-3 h-3 mr-0.5 flex-shrink-0" />
                            <span className={`${lang === 'chinese' ? 'font-normal' : ''} truncate`}>
                              <span className="sm:hidden">Continue</span>
                              <span className="hidden sm:inline">{t('history.continue')}</span>
                            </span>
                          </>
                        )}
                      </Button>

                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={loadingEpisodeId === item.episodeId}
                        onClick={() => {
                          // 🔥 移动端：打开全屏播放器
                          if (allowDirectPlay) {
                            handleMobileView(item);
                          } else {
                            // 桌面端：跳转到 episodes 页面
                            window.location.href = `/episodes/${item.episodeId}`;
                          }
                        }}
                        className="text-xs px-1.5 py-1 h-7 flex-shrink-0"
                      >
                        <span className={lang === 'chinese' ? 'font-normal' : ''}>
                          <span className="sm:hidden">View</span>
                          <span className="hidden sm:inline">{t('history.view')}</span>
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </>
  );
}
